import Cookies from "js-cookie";

/**
 * Custom hook for managing local storage and cookies.
 */
const useStorageManager = () => {
  /**
   * Stores a value in local storage.
   * @param {string} key - The key to store the value under.
   * @param {any} value - The value to store.
   */
  const saveToLocalStorage = (key: string, value: any) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  /**
   * Retrieves a value from local storage.
   * @param {string} key - The key to retrieve the value from.
   * @returns {any|null} - The retrieved value, or null if not found.
   */
  const getFromLocalStorage = (key: string) => {
    const item = localStorage.getItem(key);
    return item ? item : null;
  };

  /**
   * Removes a value from local storage.
   * @param {string} key - The key to remove the value from.
   */
  const removeFromLocalStorage = (key: string) => {
    localStorage.removeItem(key);
  };

  /**
   * Retrieves a cookie value by key.
   * @param {string} key - The cookie key to retrieve.
   * @returns {string|null} - The cookie value, or null if not found.
   */
  const getCookie = (key: string) => {
    return Cookies.get(key) || null;
  };

  /**
   * Checks if a cookie exists for the given key.
   * @param {string} key - The cookie key to check.
   * @returns {boolean} - Whether the cookie exists.
   */
  const checkCookie = (key: string) => {
    return !!getCookie(key);
  };

  /**
   * Deletes a cookie for the given key.
   * @param {string} key - The cookie key to delete.
   */
  const deleteCookie = (key: string) => {
    Cookies.remove(key, { path: "/" });
  };

  /**
   * Sets a cookie with the given key and value.
   * @param {string} key - The cookie key.
   * @param {string} value - The cookie value.
   */
  const setCookie = (key: string, value: string) => {
    deleteCookie(key); // Ensure no duplicate cookies exist.
    Cookies.set(key, value, { path: "/", secure: true, sameSite: "strict" });
  };

  return {
    saveToLocalStorage,
    getFromLocalStorage,
    removeFromLocalStorage,
    getCookie,
    checkCookie,
    deleteCookie,
    setCookie,
  };
};

export default useStorageManager;
